import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { animate, motion, useMotionValue } from 'framer-motion';
import type { CompiledBannerConfig, CompiledBannerImage } from '@shared/types/domainConfig';
import { cn } from '@shared/lib/cn';
import { trackBannerClick } from '@shared/lib/trackBannerClick';
import { openLink } from '@shared/lib/openLink';

/**
 * Auto-rotating banner. Driven by `CompiledBannerConfig` from the
 * tenant app experience. A tap on an app screen's path (`/app/events`) navigates in
 * the app; any other link opens through `openLink`, whatever `link_target` says.
 */
interface BannerCarouselProps {
  config?: CompiledBannerConfig;
  className?: string;
}

export function BannerCarousel({ config, className }: BannerCarouselProps): JSX.Element | null {
  const images = (config?.images ?? [])
    .filter((image) => String(image.image_url ?? '').trim().length > 0)
    .slice()
    .sort((a, b) => a.display_order - b.display_order);
  const navigate = useNavigate();
  const [index, setIndex] = useState(0);
  const [width, setWidth] = useState(0);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const x = useMotionValue(0);
  const canSlide = images.length > 1;

  useEffect(() => {
    if (!config?.auto_rotate || !canSlide) return;
    const duration = images[index]?.duration_ms ?? config.transition_duration_ms ?? 5000;
    const timer = window.setTimeout(() => setIndex((i) => (i + 1) % images.length), duration);
    return () => clearTimeout(timer);
  }, [canSlide, config, images, index]);

  useEffect(() => {
    const node = containerRef.current;
    if (!node) return;

    const updateWidth = () => setWidth(node.clientWidth);
    updateWidth();

    const observer = new ResizeObserver(updateWidth);
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const controls = animate(x, -index * width, {
      type: 'spring',
      stiffness: 420,
      damping: 38,
      mass: 0.75,
    });
    return () => controls.stop();
  }, [index, width, x]);

  if (images.length === 0) return null;

  const current = images[index]!;

  const goTo = (nextIndex: number) => {
    if (!canSlide) return;
    setIndex((nextIndex + images.length) % images.length);
  };

  const goNext = () => goTo(index + 1);
  const goPrev = () => goTo(index - 1);

  const onClick = (image: CompiledBannerImage) => {
    if (!image.link_url) return;

    trackBannerClick({
      analytics_tag: image.analytics_tag,
      banner_id: image.id,
      banner_key: config?.banner_key,
    });

    // A root-relative path (not protocol-relative `//host`) is a screen in this app.
    if (/^\/(?!\/)/.test(image.link_url)) navigate(image.link_url);
    else void openLink(image.link_url);
  };

  return (
    <div ref={containerRef} className={cn('relative w-full aspect-[16/9] overflow-hidden rounded-xl bg-sunken hairline', className)}>
      <motion.div
        className={cn('flex h-full touch-pan-y', canSlide ? 'cursor-grab active:cursor-grabbing' : '')}
        style={{ x }}
        drag={canSlide ? 'x' : false}
        dragConstraints={{
          left: -Math.max(images.length - 1, 0) * width,
          right: 0,
        }}
        dragElastic={0.18}
        onDragEnd={(_, info) => {
          const swipe = info.offset.x + info.velocity.x * 0.18;
          if (swipe < -64) {
            goNext();
          } else if (swipe > 64) {
            goPrev();
          } else {
            animate(x, -index * width, {
              type: 'spring',
              stiffness: 420,
              damping: 38,
              mass: 0.75,
            });
          }
        }}
      >
        {images.map((image) => (
          <div
            key={image.id}
            className="h-full w-full shrink-0"
          >
            <img src={image.image_url} alt={image.alt_text ?? ''} className="h-full w-full object-cover" />
          </div>
        ))}
      </motion.div>

      {current.link_url ? (
        <button
          type="button"
          onClick={() => onClick(current)}
          className="absolute bottom-3 right-3 z-20 rounded-lg border border-white/30 bg-ink/72 px-3 py-1.5 text-[0.7rem] font-semibold text-white shadow-[0_10px_22px_-14px_rgba(17,24,39,0.65)] backdrop-blur-md transition hover:bg-ink/82 active:scale-[0.98]"
          aria-label="Open banner link"
        >
          View
        </button>
      ) : null}

      {images.length > 1 && config?.show_indicators !== false ? (
        <div
          className="absolute bottom-3 left-1/2 z-10 flex -translate-x-1/2 gap-1.5"
        >
          {images.map((img, i) => (
            <button
              key={img.id}
              type="button"
              aria-label={`Slide ${i + 1}`}
              onClick={() => setIndex(i)}
              className={cn(
                'h-1.5 rounded-pill transition-all duration-fast',
                i === index ? 'w-6 bg-white/90' : 'w-1.5 bg-white/50',
              )}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}
